import { motion } from "framer-motion";
import {
  Megaphone, BookOpen, Star, Layers, ArrowRight,
  Sparkles, Target, Trophy, BarChart3, LayoutGrid,
} from "lucide-react";
import { services } from "@/data/siteData";

const iconMap = {
  "influencer-campaigns": Megaphone,
  "story-led-campaigns": BookOpen,
  "celebrity-endorsements": Star,
  "sequential-campaigns": Layers,
};

/* Tag → icon + accent treatment */
const tagMeta = {
  CORE: { icon: Sparkles, accent: "blue" },
  NARRATIVE: { icon: Target, accent: "indigo" },
  PRESTIGE: { icon: Trophy, accent: "amber" },
  SCALE: { icon: BarChart3, accent: "blue" },
};

/* Per-service contextual CTA text */
const ctaMap = {
  "influencer-campaigns": "Explore Campaign",
  "story-led-campaigns": "See How It Works",
  "celebrity-endorsements": "View Strategy",
  "sequential-campaigns": "See How It Works",
};

/* Premium pill tag badge with icon */
function TagBadge({ tag, onDark = false }) {
  const meta = tagMeta[tag] || { icon: Sparkles };
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 ${
        onDark
          ? "bg-white/15 border border-white/25 text-white"
          : "bg-blue-50 dark:bg-blue-500/10 border border-blue-100 dark:border-blue-400/20 text-[#2563EB] dark:text-blue-200"
      }`}
    >
      <Icon className="w-3 h-3" />
      <span className="font-body text-[10.5px] tracking-[0.22em] uppercase font-bold">
        {tag}
      </span>
    </span>
  );
}

/* Premium ghost-pill CTA — animated underline + arrow translate + slight scale */
function GhostCTA({ label, onDark = false, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      className={`group/cta relative inline-flex items-center gap-2 rounded-full pl-4 pr-3 py-2 text-[13px] font-semibold transition-all duration-300 ease-out hover:scale-[1.04] ${
        onDark
          ? "bg-white/10 hover:bg-white border border-white/30 hover:border-white text-white hover:text-[#1E3A8A] shadow-[0_4px_14px_-8px_rgba(0,0,0,0.3)] hover:shadow-[0_14px_32px_-12px_rgba(255,255,255,0.45)]"
          : "bg-white dark:bg-white/5 hover:bg-[#2563EB] dark:hover:bg-[#2563EB] border border-slate-200 dark:border-white/10 hover:border-[#2563EB] text-slate-800 dark:text-slate-100 hover:text-white dark:hover:text-white shadow-[0_4px_14px_-8px_rgba(15,23,42,0.15)] hover:shadow-[0_14px_32px_-12px_rgba(37,99,235,0.55)]"
      }`}
    >
      <span className="relative">
        {label}
        {/* Animated underline */}
        <span
          aria-hidden
          className={`absolute -bottom-0.5 left-0 h-px w-0 group-hover/cta:w-full transition-all duration-300 ease-out ${
            onDark ? "bg-[#1E3A8A]" : "bg-white"
          }`}
        />
      </span>
      <span
        className={`inline-flex w-6 h-6 rounded-full items-center justify-center transition-all duration-300 ease-out group-hover/cta:translate-x-1 ${
          onDark
            ? "bg-white/20 group-hover/cta:bg-[#2563EB] group-hover/cta:text-white"
            : "bg-blue-50 dark:bg-blue-500/15 text-[#2563EB] dark:text-blue-300 group-hover/cta:bg-white group-hover/cta:text-[#2563EB]"
        }`}
      >
        <ArrowRight className="w-3.5 h-3.5" />
      </span>
    </button>
  );
}

export default function Services() {
  const primary = services[0];
  const secondary = services.slice(1);
  const PrimaryIcon = iconMap[primary.id];

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-16 md:py-20 px-6 md:px-10 surface-2"
    >
      <div className="mx-auto max-w-7xl">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            {/* Section eyebrow as styled pill badge */}
            <div className="inline-flex items-center gap-2 rounded-full bg-white dark:bg-white/5 border border-blue-100 dark:border-white/10 shadow-[0_4px_18px_-10px_rgba(37,99,235,0.3)] px-3.5 py-1.5">
              <span className="w-5 h-5 rounded-full bg-[#2563EB] flex items-center justify-center text-white">
                <LayoutGrid className="w-3 h-3" />
              </span>
              <span className="font-body text-[11px] tracking-[0.28em] uppercase font-bold text-[#1E3A8A] dark:text-blue-200">
                What We Do
              </span>
            </div>
            <h2 className="mt-5 font-heading font-bold text-4xl sm:text-5xl lg:text-[56px] tracking-tight leading-[1.05] max-w-3xl text-slate-900 dark:text-white">
              Campaigns that <span className="gradient-text">move markets.</span>
            </h2>
          </div>
          <p className="font-body max-w-sm text-slate-600 dark:text-slate-300">
            Four engines. One outcome: cultural and commercial momentum for
            the brands we choose to work with.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-5 gap-5">
          {/* Primary — Influencer Campaigns */}
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            data-testid={`services-card-${primary.id}`}
            className="group relative overflow-hidden rounded-3xl lg:col-span-3 min-h-[420px] lg:min-h-[560px] p-8 md:p-10 text-white"
            style={{
              backgroundImage:
                "linear-gradient(135deg, rgba(11,28,61,0.92) 0%, rgba(30,58,138,0.85) 50%, rgba(37,99,235,0.78) 100%), url('https://customer-assets.emergentagent.com/job_regional-creator-hub/artifacts/fqohd3jb_Gemini_Generated_Image_eo8ka6eo8ka6eo8k%20%281%29.png')",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-blue-400/30 blur-3xl opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative flex flex-col h-full">
              <div className="flex items-center justify-between gap-3">
                <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/25">
                  <PrimaryIcon className="w-6 h-6 text-white" />
                </div>
                <div className="flex items-center gap-2">
                  <TagBadge tag={primary.tag} onDark />
                  <span className="rounded-full px-2.5 py-1 bg-white/10 border border-white/20 font-body text-[10px] tracking-[0.2em] uppercase font-bold">
                    Flagship
                  </span>
                </div>
              </div>

              <h3 className="mt-12 md:mt-20 font-heading font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-xl">
                5+ Celebrities
              </h3>

              <div className="mt-6 flex flex-wrap gap-x-10 gap-y-4">
                <div>
                  <div className="font-body text-[10.5px] tracking-[0.24em] uppercase text-blue-100/85 font-bold">
                    Reach
                  </div>
                  <div className="mt-1 font-heading font-extrabold text-3xl md:text-4xl leading-none tracking-tight">
                    10M<span className="text-blue-200">+</span>
                  </div>
                </div>
                <div>
                  <div className="font-body text-[10.5px] tracking-[0.24em] uppercase text-blue-100/85 font-bold">
                    Engagement
                  </div>
                  <div className="mt-1 font-heading font-extrabold text-3xl md:text-4xl leading-none tracking-tight">
                    12<span className="text-blue-200">%</span>
                  </div>
                </div>
              </div>

              <ul className="mt-8 flex flex-wrap gap-2">
                {primary.bullets.map((b) => (
                  <li
                    key={b}
                    className="font-body rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs text-white/90"
                  >
                    {b}
                  </li>
                ))}
              </ul>

              <div className="mt-auto pt-8">
                <GhostCTA
                  label={ctaMap[primary.id]}
                  onDark
                  testId={`services-cta-${primary.id}`}
                />
              </div>
            </div>
          </motion.article>

          {/* Secondary — 3 stacked */}
          <div className="lg:col-span-2 grid gap-5 grid-rows-3">
            {secondary.map((s, i) => {
              const Icon = iconMap[s.id];
              return (
                <motion.article
                  key={s.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.55, delay: 0.1 + i * 0.08 }}
                  data-testid={`services-card-${s.id}`}
                  className="group relative overflow-hidden rounded-3xl bg-white dark:bg-white/5 border border-slate-200 dark:border-white/10 p-6 md:p-7 hover:border-blue-400 hover:-translate-y-0.5 hover:shadow-[0_20px_50px_-30px_rgba(37,99,235,0.3)] transition-all duration-300 flex flex-col"
                >
                  <div className="relative flex items-start justify-between gap-3">
                    <div className="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-500/15 border border-blue-100 dark:border-blue-400/20 flex items-center justify-center text-[#2563EB] dark:text-blue-300">
                      <Icon className="w-5 h-5" />
                    </div>
                    <TagBadge tag={s.tag} />
                  </div>
                  <h3 className="relative mt-5 font-heading font-bold text-xl md:text-[22px] text-slate-900 dark:text-white leading-tight">
                    {s.title}
                  </h3>
                  <p className="font-body relative mt-1.5 text-sm text-slate-600 dark:text-slate-300 leading-relaxed flex-1">
                    {s.description}
                  </p>
                  <div className="relative mt-5">
                    <GhostCTA
                      label={ctaMap[s.id]}
                      testId={`services-cta-${s.id}`}
                    />
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>

        <p
          data-testid="services-footer-line"
          className="font-body mt-12 text-center font-heading font-medium text-lg md:text-xl text-slate-600 dark:text-slate-300"
        >
          Everything built to make your brand{" "}
          <span className="gradient-text font-bold">impossible to ignore.</span>
        </p>
      </div>
    </section>
  );
}
